import { useMutation } from '@tanstack/react-query';

import { apiClient, createNfctronApiRequestParams } from '@root/features/api-client/api-client';
import { useApiClient } from '@root/features/api-client/api-client.context';
import { useSessionStore } from '@root/features/authentication/stores/sessionStore';
import { type UserAccount } from '@root/features/authentication/types/userAccount';

/** interface for login credentials */
interface LoginCredentials {
	email: string;
	password: string;
}

/** custom hook for login functionality */
export const useLogin = () => {
	/** get session store methods */
	const { setAccount, setIsAuthenticated } = useSessionStore();

	/** get api clients */
	const { passApi, rootApi } = useApiClient();

	/** return mutation hook */
	return useMutation({
		/** mutation function to handle user login */
		mutationFn: async (credentials: LoginCredentials) => {
			/** send POST request to login endpoint */
			const res = await apiClient.post('/api/auth/login', credentials, {
				baseURL: '/',
				...createNfctronApiRequestParams(passApi.nfctronApiParams ?? {}),
			});

			/** return user account data */
			return res.data.userAccount as UserAccount;
		},
		/** success callback to update session state */
		onSuccess: (userAccount) => {
			setAccount(userAccount);
			setIsAuthenticated(true);

			/** update passApi and rootApi clients with security data */
			passApi.setSecurityData(userAccount);
			rootApi.setSecurityData(userAccount);
		},
		/** error callback to handle login errors */
		onError: (error: Error) => {
			console.error('Login error:', error.message);
		},
	});
};
